import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, FormikProps } from 'formik'
import { Button } from 'primereact/button'
import {
  Column,
  ColumnBodyOptions,
  ColumnEditorOptions,
} from 'primereact/column'
import {
  DataTableDataSelectableEvent,
  DataTableRowEditCompleteEvent,
  DataTableRowEditEvent,
  DataTableRowEditSaveEvent,
  DataTableRowEditValidatorOptions,
  DataTableValue,
  DataTableValueArray,
} from 'primereact/datatable'
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown'
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber'
import { classNames } from 'primereact/utils'

import { NumberBodyTemplate } from '../../../../../../../components/tableTemplates'
import {
  DeliveryForecastDespatchAdviceItemBatch,
  DeliveryForecastDespatchAdvicePackagingBatch,
} from '../../../../../../../data/models'
import {
  ConfigurableComponent,
  ConfigurableTable,
} from '../../../../../../../utils/wrapper'
import {
  FormFieldConfig,
  PackagingItem,
  ReadonlyFieldConfig,
} from '../../../../../../ui-config/data'
import { FormFieldName } from '../FormFieldName'
import { FormValues } from '../FormValues'

type Props = {
  config: PackagingItem<ReadonlyFieldConfig | FormFieldConfig>
  formik: FormikProps<FormValues>
  batchOptions?: DeliveryForecastDespatchAdvicePackagingBatch[]
  addBatchOptions: (
    newBatches: DeliveryForecastDespatchAdvicePackagingBatch[],
  ) => void
  isFormFieldValid: (name: FormFieldName, index?: number) => boolean
  getFormErrorMessage: (
    name: FormFieldName,
    index?: number,
  ) => false | JSX.Element
  children?: JSX.Element
}

export const BatchesTableView: React.FC<Props> = ({
  config,
  formik,
  batchOptions,
  addBatchOptions,
  isFormFieldValid,
  getFormErrorMessage,
  children,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const [originalRowData, setOriginalRowData] =
    useState<DeliveryForecastDespatchAdvicePackagingBatch | null>(null)
  const [editingRowBatchnumber, setEditingRowBatchnumber] = useState<
    string | null | undefined
  >(null)
  const [isNewRow, setIsNewRow] = useState(false)

  const [selectedBatches, setSelectedBatches] =
    useState<DeliveryForecastDespatchAdvicePackagingBatch[]>()

  const getBatchnumberOptions = (options?: ColumnEditorOptions) => {
    const currentBatchNumber = options?.value
    const usedBatchnumbers = formik.values.batches
      .filter(batch => batch.Batchnumber !== currentBatchNumber)
      .map(batch => batch.Batchnumber)

    const notUsedBatchnumbers = batchOptions
      ?.filter(batch => {
        return (
          !usedBatchnumbers.includes(batch.Batchnumber) ||
          batch.Batchnumber === currentBatchNumber
        )
      })
      .map(batch => ({ ...batch, BatchId: batch.Id }))

    return notUsedBatchnumbers
  }

  const handleAddNewRow = async () => {
    const newRowIndex = formik.values.batches?.length || 0

    const options = getBatchnumberOptions()

    const newRowData = options?.[0]

    if (newRowData) {
      newRowData.Id = `newBatch-${newRowIndex}`
    }

    await formik.setFieldValue(`batches.${newRowIndex}`, newRowData)
    setEditingRowBatchnumber(newRowData?.Batchnumber)
    setIsNewRow(true)
  }

  const handleDeleteRows = async () => {
    if (selectedBatches && selectedBatches?.length > 0) {
      const selectedBatchnumbers = selectedBatches.map(
        batch => batch.Batchnumber,
      )

      // Remove batches from formik
      const newBatches = formik.values.batches.filter(
        batch => !selectedBatchnumbers.includes(batch.Batchnumber),
      )

      await formik.setFieldValue('batches', newBatches)
      setSelectedBatches([])

      // Add the removed batches to batch options so the user can add them again
      addBatchOptions(selectedBatches)
    }
  }

  const isAddButtonDisabled = (): boolean => {
    // Filter out batchOptions that are already used in formik.values.batches
    const hasBatchOptions = batchOptions?.filter(
      batchOption =>
        !formik.values.batches.some(
          formikBatch => formikBatch.BatchId === batchOption.BatchId,
        ),
    )

    return hasBatchOptions?.length === 0
  }

  const isBatchesTableVisible = (): boolean => {
    const hasFormikBatches = formik.values.batches.length > 0
    const hasBatchesForSelection = !isAddButtonDisabled()
    const isVisible = hasFormikBatches || hasBatchesForSelection

    return isVisible
  }

  const headerActions = (
    <>
      <Button
        label={t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.batches.table.delete.label',
        )}
        onClick={handleDeleteRows}
        icon="pi pi-trash"
        outlined
        disabled={!!editingRowBatchnumber || !selectedBatches?.length}
      />

      <Button
        label={t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.batches.table.add.label',
        )}
        onClick={handleAddNewRow}
        icon="pi pi-plus"
        outlined
        disabled={!!editingRowBatchnumber || isAddButtonDisabled()}
      />
    </>
  )

  const batchnumberEditor = (options: ColumnEditorOptions) => {
    const { rowIndex } = options

    const selectedBatchTemplate = (
      option: DeliveryForecastDespatchAdvicePackagingBatch,
      props: any,
    ) => {
      if (option) {
        return <>{batchOptionTemplate(option)}</>
      }

      return <>{props?.placeholder}</>
    }

    const batchOptionTemplate = (
      option: DeliveryForecastDespatchAdvicePackagingBatch,
    ) => {
      return <>{option.Batchnumber}</>
    }

    const handleBatchnumberChange = (event: DropdownChangeEvent) => {
      const { value } = event
      const selectedBatch = batchOptions?.find(
        batch => batch.Batchnumber === value,
      )

      if (selectedBatch) {
        formik.setFieldValue(`batches.${rowIndex}`, selectedBatch)
        setEditingRowBatchnumber(selectedBatch.Batchnumber)
      }
    }

    // For the dropdown only show options that are not already taken
    // otherwise it is possible to add the same batch again
    const selectedBatch = formik.values.batches?.[rowIndex]
    const batchOptionsWithoutFormikBatches =
      batchOptions?.filter(
        batchOption =>
          !formik.values.batches.some(
            formikBatch => formikBatch.BatchId === batchOption.BatchId,
          ),
      ) || []
    const batchOptionsWithSelectedBatch = [
      ...batchOptionsWithoutFormikBatches,
      selectedBatch,
    ]

    const batchnumberComponent = (
      <Dropdown
        id="Batchnumber"
        className={classNames('w-full', {
          'p-invalid': !isFormFieldValid('Batchnumber', rowIndex),
        })}
        value={formik.values.batches?.[rowIndex]?.Batchnumber}
        onChange={handleBatchnumberChange}
        optionLabel="Batchnumber"
        optionValue="Batchnumber"
        itemTemplate={batchOptionTemplate}
        valueTemplate={selectedBatchTemplate}
        options={batchOptionsWithSelectedBatch}
        placeholder="-"
        autoFocus
      />
    )

    const batchnumberErrorMsg = (
      <div>{getFormErrorMessage('Batchnumber', rowIndex)}</div>
    )

    const batchnumberWrapper = (
      <ConfigurableComponent
        key={`batchnumber-${rowIndex}`}
        config={config}
        component={batchnumberComponent}
        errorMsg={batchnumberErrorMsg}
        className="m-0"
      />
    )

    return <>{batchnumberWrapper}</>
  }

  const quantityEditor = (options: ColumnEditorOptions) => {
    const { rowData, rowIndex } = options
    const minValue = 1
    const maxValue = rowData.OpenQuantity || 0

    const handleQuantityChange = (event: InputNumberChangeEvent) => {
      const value = event.value || 0

      if (value > maxValue) {
        formik.setFieldValue(`batches.${rowIndex}.Quantity`, maxValue)
      } else if (value < minValue) {
        formik.setFieldValue(`batches.${rowIndex}.Quantity`, minValue)
      } else {
        formik.setFieldValue(`batches.${rowIndex}.Quantity`, value)
      }
    }

    const quantityComponent = (
      <InputNumber
        id="Quantity"
        className="w-full"
        value={formik.values.batches?.[rowIndex].Quantity}
        onChange={handleQuantityChange}
        locale={language}
        showButtons
        min={minValue}
        max={maxValue}
      />
    )

    const quantityErrorMsg = (
      <div>{getFormErrorMessage('Quantity', rowIndex)}</div>
    )

    const quantityWrapper = (
      <ConfigurableComponent
        config={config}
        component={quantityComponent}
        errorMsg={quantityErrorMsg}
        className="m-0"
      />
    )

    return <>{quantityWrapper}</>
  }

  const availableQuantityEditor = (options: ColumnEditorOptions) => {
    const { rowIndex } = options

    const batch = formik.values.batches?.[rowIndex]
    const currentQuantity = batch.Quantity || 0
    const openQuantity = batch.OpenQuantity || 0
    const availableQuantity = openQuantity - currentQuantity

    return (
      <InputNumber
        id="AvailableQuantity"
        className="w-full"
        value={availableQuantity}
        locale={language}
        disabled
      />
    )
  }

  const actionBodyTemplate = (
    rowData: DeliveryForecastDespatchAdviceItemBatch,
    options: ColumnBodyOptions,
  ) => {
    const isDisabled = isNewRow

    if (options.rowEditor?.editing)
      return (
        <div className="flex">
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-text"
            onClick={options.rowEditor?.onCancelClick}
          />

          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text"
            onClick={options.rowEditor?.onSaveClick}
          />
        </div>
      )

    return (
      <div className="flex">
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text"
          severity="danger"
          onClick={() => handleDelete(rowData, options.rowIndex)}
          disabled={isDisabled}
        />

        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={options.rowEditor?.onInitClick}
          disabled={isDisabled}
        />
      </div>
    )
  }

  const buttons = (
    <Column
      rowEditor
      key="buttons"
      body={(e, options) => actionBodyTemplate(e, options)}
      align="right"
    ></Column>
  )

  const handleDelete = async (
    rowData: DeliveryForecastDespatchAdvicePackagingBatch,
    index: number,
  ) => {
    const newBatches = formik.values.batches.filter(
      batch => batch.Batchnumber !== rowData.Batchnumber,
    )
    await formik.setFieldValue('batches', newBatches)
    await formik.setFieldTouched(`batches.${index}.Batchnumber`, false)
    await formik.setFieldTouched(`batches.${index}.Quantity`, false)

    handleDeselection([rowData])
  }

  const handleDeselection = (
    batches: DeliveryForecastDespatchAdvicePackagingBatch[],
  ) => {
    const newSelectedBatches = selectedBatches?.filter(
      batch =>
        !batches.some(
          deselectedBatch => deselectedBatch.Batchnumber === batch.Batchnumber,
        ),
    )

    setSelectedBatches(newSelectedBatches)
  }

  const onRowEditInit = (event: DataTableRowEditEvent) => {
    const { data } = event
    setOriginalRowData(data as DeliveryForecastDespatchAdvicePackagingBatch)
    setEditingRowBatchnumber(data.Batchnumber)
  }

  const onRowEditChange = (event: DataTableRowEditEvent) => {}

  const onRowEditCancel = async (event: DataTableRowEditEvent) => {
    const { index } = event
    await formik.setFieldTouched(`batches.${index}.Batchnumber`, false)
    await formik.setFieldTouched(`batches.${index}.Quantity`, false)

    // If the batch is newly added we need to remove this from formik.
    // Otherwise it was already added but the user wants to cancel the editing and remove the changes.
    if (isNewRow) {
      const updatedBatches = formik.values.batches?.filter(
        batch => batch.Batchnumber !== event.data.Batchnumber,
      )
      formik.setFieldValue('batches', updatedBatches)
    } else {
      formik.setFieldValue(`batches.${event.index}`, originalRowData)
    }

    setEditingRowBatchnumber(null)
    setIsNewRow(false)
  }

  const rowEditValidator = (
    data: DataTableValue,
    options: DataTableRowEditValidatorOptions<DataTableValueArray>,
  ) => {
    const rowErrors =
      Array.isArray(formik.errors.batches) &&
      (formik.errors.batches[
        options.rowIndex
      ] as FormikErrors<DeliveryForecastDespatchAdviceItemBatch>)

    const isValid = !rowErrors || Object.keys(rowErrors).length === 0

    return isValid
  }

  const onRowEditSave = async (
    event: DataTableRowEditSaveEvent<DataTableValueArray>,
  ) => {
    const { index } = event
    await formik.setFieldTouched(`batches.${index}.Batchnumber`, true)
    await formik.setFieldTouched(`batches.${index}.Quantity`, true)
  }

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    setEditingRowBatchnumber(null)
    setIsNewRow(false)
  }

  const isDataSelectable = (
    event: DataTableDataSelectableEvent,
  ): boolean | null => {
    const isNotActiveEditingRow =
      event.data.Batchnumber !== editingRowBatchnumber
    return isNotActiveEditingRow
  }

  if (!isBatchesTableVisible()) return null

  return (
    <>
      <ConfigurableTable
        config={config.batchTable}
        data={formik.values.batches}
        headerTitle={t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.batches.table.title',
        )}
        headerActions={headerActions}
        stateKey=""
        buttons={buttons}
        checkboxen
        isDataSelectable={isDataSelectable}
        selected={selectedBatches}
        setSelected={setSelectedBatches}
        dataKey="Batchnumber"
        editMode="row"
        editingRows={{ [String(editingRowBatchnumber)]: true }}
        // Callbacks
        onRowEditInit={onRowEditInit}
        onRowEditChange={onRowEditChange}
        onRowEditCancel={onRowEditCancel}
        rowEditValidator={rowEditValidator}
        onRowEditSave={onRowEditSave}
        onRowEditComplete={onRowEditComplete}
        className="mt-4"
      >
        <Column
          key="batchnumber"
          field="Batchnumber"
          header="Batchnumber"
          editor={options => batchnumberEditor(options)}
          className="w-4"
        ></Column>
        <Column
          key="quantity"
          field="Quantity"
          header="Quantity"
          editor={options => quantityEditor(options)}
          body={(rowData: DeliveryForecastDespatchAdvicePackagingBatch) =>
            NumberBodyTemplate(rowData.Quantity)
          }
          className="w-4"
        ></Column>
        <Column
          key="availableQuantity"
          field="AvailableQuantity"
          header="Available quantity"
          body={(rowData: DeliveryForecastDespatchAdvicePackagingBatch) => {
            const availableQuantity = rowData.OpenQuantity! - rowData.Quantity
            return <>{NumberBodyTemplate(availableQuantity)}</>
          }}
          editor={availableQuantityEditor}
          className="w-4"
        ></Column>
      </ConfigurableTable>

      {children}
    </>
  )
}
